import { format, isAfter, isBefore, isValid, parse, startOfDay } from 'date-fns'

import { DATE_ONLY_FORMAT, resolveDateOnlyFromString } from '@/utils/dateString'

export type ProductDateRangeValidation = {
  translationKey:
    | 'Grid.validation.startDateAfterEndDate'
    | 'Grid.validation.endDateBeforeStartDate'
  fallbackMessage: string
}

export const normalizeProductDateValue = (
  value?: string | Date | null,
): Date | null => {
  if (!value) return null

  if (value instanceof Date) {
    return startOfDay(value)
  }

  const dateOnly = resolveDateOnlyFromString(value)
  if (!dateOnly) return null

  const formattedDate = parse(dateOnly, DATE_ONLY_FORMAT, new Date())
  if (
    isValid(formattedDate) &&
    format(formattedDate, DATE_ONLY_FORMAT) === dateOnly
  ) {
    return startOfDay(formattedDate)
  }

  return null
}

export const serializeProductDateValue = (
  value?: string | Date | null,
): string => {
  const normalized = normalizeProductDateValue(value)
  return normalized
    ? `${format(normalized, DATE_ONLY_FORMAT)}T00:00:00.000Z`
    : ''
}

export const validateProductDateRange = (params: {
  field: 'startDate' | 'endDate'
  value?: string | Date | null
  otherValue?: string | Date | null
}): ProductDateRangeValidation | null => {
  const { field, value, otherValue } = params
  const parsedValue = normalizeProductDateValue(value)
  const parsedOtherValue = normalizeProductDateValue(otherValue)

  if (!parsedValue || !parsedOtherValue) return null

  if (field === 'startDate' && isAfter(parsedValue, parsedOtherValue)) {
    return {
      translationKey: 'Grid.validation.startDateAfterEndDate',
      fallbackMessage:
        'La date de début ne peut pas être supérieure à la date de fin',
    }
  }

  if (field === 'endDate' && isBefore(parsedValue, parsedOtherValue)) {
    return {
      translationKey: 'Grid.validation.endDateBeforeStartDate',
      fallbackMessage:
        'La date de fin ne peut pas être inférieure à la date de début',
    }
  }

  return null
}

export const validateProductDateRangeForRow = (params: {
  startDate?: string | Date | null
  endDate?: string | Date | null
}): ProductDateRangeValidation | null => {
  const { startDate, endDate } = params

  return (
    validateProductDateRange({
      field: 'startDate',
      value: startDate,
      otherValue: endDate,
    }) ??
    validateProductDateRange({
      field: 'endDate',
      value: endDate,
      otherValue: startDate,
    })
  )
}
